import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { DataTable } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import moment from "moment";
import { GET_PAYMENT_LINK_PAYMENTS } from "graphql/paymentLink";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { formatCashFull } from "utils/formatCash";
import { PaymentLinkPaymentStatus } from "enums/PaymentLink";

interface PaymentLinkPaymentRow {
  id: string;
  status: PaymentLinkPaymentStatus;
  payerEmail: string;
  payerName?: string;
  totalCharged: number;
  feeCoverAmount?: number;
  tipAmount?: number;
  discountAmount?: number;
  currency: string;
  createdAt?: number;
}

interface PaymentLinkPaymentsQueryData {
  paymentLinkPayments?: {
    edges?: { node: PaymentLinkPaymentRow }[];
  };
}

interface PaymentLinkPaymentsTableProps {
  paymentLinkId: string;
  limit?: number;
}

const PaymentLinkPaymentsTable: React.FC<PaymentLinkPaymentsTableProps> = ({
  paymentLinkId,
  limit = 25,
}) => {
  const { t } = useTranslation();
  const {
    dataArray: payments,
    displayLoading,
    handleScroll,
    isInfiniteScrollFetching,
  } = useInfiniteScroll({
    query: GET_PAYMENT_LINK_PAYMENTS,
    initialVariables: { paymentLinkId, first: limit },
    extractData: extractPayments,
  });

  const anyTips = payments.some((payment) => (payment.tipAmount || 0) > 0);

  const columns: DataColumn<PaymentLinkPaymentRow>[] = useMemo(
    () => [
      {
        id: "payer",
        header: t("Payer"),
        maxWidth: 160,
        cell: (payment) => payment.payerName || payment.payerEmail,
      },
      {
        id: "amount",
        header: t("Amount"),
        cell: (payment) =>
          formatCashFull(payment.totalCharged, payment.currency),
      },
      ...(anyTips
        ? [
            {
              id: "tip",
              header: t("Tip"),
              cell: (payment: PaymentLinkPaymentRow) =>
                payment.tipAmount
                  ? formatCashFull(payment.tipAmount, payment.currency)
                  : "",
            },
          ]
        : []),
      {
        id: "status",
        header: t("Status"),
        cell: (payment) => t(titleFor(payment.status)),
      },
      {
        id: "createdAt",
        header: t("Date"),
        cell: (payment) =>
          payment.createdAt ? moment(payment.createdAt).format("ll") : "",
      },
    ],
    [t, anyTips],
  );

  return (
    <DataTable<PaymentLinkPaymentRow>
      columns={columns}
      rows={payments}
      getRowKey={(payment) => payment.id}
      loading={displayLoading}
      isFetchingMore={isInfiniteScrollFetching}
      onScroll={handleScroll}
      maxHeight="calc(100vh - 220px)"
      emptyState={
        <Typography
          variant="body2"
          sx={{ color: "var(--color-text-secondary)" }}
        >
          {t("No payments yet")}
        </Typography>
      }
      loadingMoreText={t("Loading more payments")}
      stickyHeader
    />
  );
};

function extractPayments(
  data: PaymentLinkPaymentsQueryData,
): PaymentLinkPaymentRow[] {
  return data?.paymentLinkPayments?.edges?.map((edge) => edge.node) || [];
}

function titleFor(status: PaymentLinkPaymentStatus): string {
  switch (status) {
    case PaymentLinkPaymentStatus.SUCCEEDED:
      return "Paid";
    case PaymentLinkPaymentStatus.PROCESSING:
      return "Processing";
    case PaymentLinkPaymentStatus.FAILED:
      return "Failed";
    case PaymentLinkPaymentStatus.REFUNDED:
      return "Refunded";
    case PaymentLinkPaymentStatus.PARTIALLY_REFUNDED:
      return "Partially Refunded";
    case PaymentLinkPaymentStatus.CANCELED:
      return "Canceled";
    default:
      return "Pending";
  }
}

export default PaymentLinkPaymentsTable;
